import { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import uniqBy from 'lodash/uniqBy';
import Link from 'next/link';

import { btnTypes } from '@r1-frontend/ui-react/components/buttons/baseButton';
import { SliderHoc } from '@r1-frontend/ui-react/components/NukaCarousel/SliderHoc';
import { EMediaWidth } from '@r1-frontend/ui-react/tokens/mediaWidth';
import { useMatchMedia } from '@r1-frontend/shared/hooks/useMatchMedia';
import { useResizeObserver } from '@r1-frontend/shared/hooks/useResizeObserver';

import { ISubscriptionItem } from '~/src/api/common-types/sale-package';
import { EShopCategory } from '~/src/api/content/shop/getShopItems';
import { USER_LOGIN_ROUTE } from '~/src/constants/routes';
import { selectProductsByCategory } from '~/src/features/full-buy/store/selectors/products/selectProductsByCategory';
import { selectCurrentTariff } from '~/src/features/full-buy/store/selectors/selectCurrentTariff';
import { TState } from '~/src/store';

import Card, { ICard } from './Card';
import CardRow from './CardRow';

import * as ST from './styled';

const shopItemTypeIDs = {
    internet: 16171,
    tvChannels: 16181,
};

const cardWidth = 294;
const cardGap = 24;

interface ITariffInclude {
    showLink?: boolean,
}

const TariffInclude = ({ showLink }: ITariffInclude): JSX.Element => {
    const parentBlock = useRef<HTMLDivElement>(null);
    const { width } = useResizeObserver(parentBlock);
    const [isShowAll, setIsShowAll] = useState(false);
    const isMobile = useMatchMedia(EMediaWidth.mobile);

    const {
        tariffPackage,
        salePackageId,
        products,
    } = useSelector((state: TState) => ({
        tariffPackage: selectCurrentTariff(state),
        salePackageId: state.fullBuy.calculatedSalePackageId,
        products: selectProductsByCategory(state, EShopCategory.Products),
    }));

    const {
        additionalProducts,
        subscriptions,
        isMono,
        salePackageLinks,
    } = tariffPackage || {};

    // Выбранный тариф с подписками "Засмотрись"
    const finalsalePackageLink = salePackageLinks?.filter(item => item.salePackageId === salePackageId) || [];
    const subscriptionsArray: (undefined | ISubscriptionItem)[] = [];

    // Получение массива подписок, которые лежат в выбранном тарифе "Засмотрись"
    const finalSubscriptions = finalsalePackageLink && finalsalePackageLink[0]?.subscriptions?.map((item) => finalsalePackageLink[0]
        ? finalsalePackageLink[0]?.subscriptions?.map(el => item.service_id === el.service_id && item)
        : null);
    finalSubscriptions?.map((item) => item?.map(el => subscriptionsArray.push(el ? el : undefined)));
    const filteredSubscriptionsArray = subscriptionsArray.filter(item => item);

    const requiredAdditionalProducts = useMemo(() => {
        const filteredProducts = additionalProducts?.filter(item => item.required);
        return uniqBy(filteredProducts, 'id');
    }, [additionalProducts]);

    const allTariffIncludes: ICard[] = useMemo(() => {
        const tariffIncludesResult: ICard[] = [];
        // @TODO Костыль для скрытия Мувикса
        products
            .filter(product =>
                product.props.find(prop => prop.name === 'name_micro' && prop.value?.toString().toLowerCase() !== 'тв-приставка'))
            .forEach(product => {
                const isMonoTariffWithTVShopItems = isMono && product.id === shopItemTypeIDs.tvChannels;
                if (!isMonoTariffWithTVShopItems) {
                    const isCover = (product.image_width && product.image_height)
                        ? product.image_width / product.image_height > 1.8
                        : true;
                    tariffIncludesResult.push({
                        img: product?.image || '',
                        webpImg: product?.image_webp || '',
                        isCover,
                        title: product?.title || '',
                        description: product?.body || product?.description || '',
                    });
                }
            });
        requiredAdditionalProducts.forEach(product => {
            const shopItem = product;
            const isCover = (shopItem && shopItem.image_width && shopItem.image_height)
                ? shopItem.image_width / shopItem.image_height > 1.8
                : true;
            tariffIncludesResult.push({
                img: shopItem?.image || '',
                webpImg: shopItem?.image_webp || '',
                isCover,
                title: shopItem?.title || '',
                description: shopItem?.body || shopItem?.description || '',
            });
        });
        {
            salePackageId && finalsalePackageLink[0]?.subscriptions
                ? filteredSubscriptionsArray.forEach(content => {
                    tariffIncludesResult.push({
                        img: content?.extend_image_for_full_buy || content?.image || '',
                        webpImg: content?.extend_image_for_full_buy_webp || content?.image_webp || '',
                        isCover: content && content?.image_width / content?.image_height > 1.8 || false,
                        title: content?.title || '',
                        description: content?.extend_body_for_full_buy || content?.body_for_full_buy || '',
                    });
                })
                : subscriptions?.forEach(content => {
                    tariffIncludesResult.push({
                        img: content?.imageForFullBuy || content?.image || '',
                        webpImg: content?.imageForFullBuyWebp || content?.image_webp || '',
                        isCover: content?.image_width / content?.image_height > 1.8,
                        title: content?.title || '',
                        description: content?.extendBodyForFullBuy || content?.bodyForFullBuy || '',
                    });
                });
        }

        return tariffIncludesResult;
    }, [subscriptions, requiredAdditionalProducts, products, isMono, salePackageId]);

    const amountInRow: number = useMemo(() => {
        if (width) {
            const countWithGap = Math.floor(width / (cardWidth + cardGap));
            const widthWithGap = ((cardWidth + cardGap) * countWithGap);
            const freeWidth = (width - widthWithGap) >= cardWidth;
            return freeWidth ? countWithGap + 1 : countWithGap;
        }
        return 2;
    }, [width]);

    const rowsCardsToShow: ICard[][] = useMemo(() => {
        const res = [];
        if (allTariffIncludes.length) {
            for (let i = 0; i < allTariffIncludes.length; i += amountInRow) {
                const chunk = allTariffIncludes.slice(i, i + amountInRow);
                res.push(chunk);
            }
            return res;
        }
        return [];
    }, [allTariffIncludes, amountInRow]);

    return (<ST.Wrapper ref={parentBlock}>
        {allTariffIncludes.length > 0 && <>
            <ST.BlockTitle>Скоро вы сможете пользоваться:</ST.BlockTitle>
            {isMobile
                ? <ST.SwiperContainer data-test='swiper-container'>
                    <SliderHoc
                        cardLength={allTariffIncludes.length}
                        needWrapGap={true}
                        withoutArrows={true}
                        slideWidth={cardWidth}
                        cellSpacing={16}
                    >
                        {allTariffIncludes.map(card => (<Card key={'card-slide-' + card.title} card={card} width={cardWidth}/>))}
                    </SliderHoc>
                </ST.SwiperContainer>
                : <ST.BlockArray>
                    {rowsCardsToShow.map((rowCards, rowIndex) => {
                        const isHideRow = !isShowAll && rowIndex > 1;
                        return !isHideRow
                            ? <CardRow
                                key={'card-row-' + rowCards[0]?.title}
                                rowCards={rowCards}
                                arrayCount={allTariffIncludes.length}
                                cardGap={cardGap}
                                cardWidth={cardWidth}
                                amountInRow={amountInRow}
                                rowIndex={rowIndex}
                                isShowAll={isShowAll}
                                onClick={() => setIsShowAll(true)}
                            />
                            : <></>;
                    })}
                </ST.BlockArray>
            }
        </>}
        {Boolean(showLink) && <Link href={USER_LOGIN_ROUTE} passHref>
            <ST.StyledButton
                type={btnTypes.SECONDARY}
                href={USER_LOGIN_ROUTE}
                text='Перейти в личный кабинет'
            />
        </Link>}
    </ST.Wrapper>);
};

export default TariffInclude;
